import {
  Pagination,
  PaginationContent,
  PaginationEllipsis,
  PaginationItem,
  PaginationLink,
  PaginationNext,
  PaginationPrevious,
} from '@/components/ui/pagination'

type PaginatorProps = {
  currentPage: number
  totalPages: number
  onPageChange: (pageNumber: number) => void
  showPreviousNext: boolean
}

export function PaginationContainer({ currentPage, totalPages, onPageChange, showPreviousNext }: PaginatorProps) {
  return (
    <Pagination>
      <PaginationContent>
        {showPreviousNext && totalPages && currentPage > 1 && (
          <PaginationItem>
            <PaginationPrevious onClick={() => onPageChange(currentPage - 1)} />
          </PaginationItem>
        )}
        {PaginationLinks(currentPage, totalPages, onPageChange)}
        {showPreviousNext && totalPages && currentPage < totalPages && (
          <PaginationItem>
            <PaginationNext onClick={() => onPageChange(currentPage + 1)} />
          </PaginationItem>
        )}
      </PaginationContent>
    </Pagination>
  )
}

const PaginationLinks = (currentPage: number, totalPages: number, onPageChange: (page: number) => void) => {
  if (totalPages <= 6) {
    return Array.from({ length: totalPages }, (_, i) => (
      <PaginationItem key={`pagination-${i}`}>
        <PaginationLink onClick={() => onPageChange(i + 1)} isActive={i + 1 === currentPage}>
          {i + 1}
        </PaginationLink>
      </PaginationItem>
    ))
  }

  const items: React.ReactNode[] = []
  const showStartEllipsis = currentPage > 3
  const showEndEllipsis = currentPage < totalPages - 2

  // 表示するページ番号のセットを作成
  const pages = new Set<number>()

  // 常に1と最後のページ
  pages.add(1)
  pages.add(totalPages)

  // 現在のページとその前後
  pages.add(currentPage)
  if (currentPage > 1) pages.add(currentPage - 1)
  if (currentPage < totalPages) pages.add(currentPage + 1)

  // 先頭省略記号がない場合は2,3,4も追加
  if (!showStartEllipsis) {
    pages.add(2)
    pages.add(3)
    pages.add(4)
  }

  // 末尾省略記号がない場合は末尾-1, 末尾-2, 末尾-3も追加
  if (!showEndEllipsis) {
    pages.add(totalPages - 1)
    pages.add(totalPages - 2)
    pages.add(totalPages - 3)
  }

  // ソートして表示
  const sortedPages = Array.from(pages)
    .filter((p) => p >= 1 && p <= totalPages)
    .sort((a, b) => a - b)

  for (let i = 0; i < sortedPages.length; i++) {
    const page = sortedPages[i]

    // 前のページとの間にギャップがあれば省略記号
    if (i > 0 && page - sortedPages[i - 1] > 1) {
      items.push(<PaginationEllipsis key={`ellipsis-${page}`} />)
    }

    items.push(
      <PaginationItem key={`pagination-${page}`}>
        <PaginationLink onClick={() => onPageChange(page)} isActive={page === currentPage}>
          {page}
        </PaginationLink>
      </PaginationItem>,
    )
  }

  return items
}
